import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { PREFECTURES } from '../utils/constants';
import { compressImageFile } from '../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { showToast } from '../utils/toast';
import { validateHttpsUrl } from '../utils/urlValidation';
import { addCircleMember } from './circle/CircleMemberList';
import './CreateCircle.css';

const ACTIVITY_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: '週1回' },
  { value: 'biweekly', label: '月2回' },
  { value: 'monthly', label: '月1回' },
  { value: 'seasonal', label: '季節ごと' },
  { value: 'irregular', label: '不定期' },
  { value: 'other', label: 'その他' },
];

const AGE_GROUP_OPTIONS = [
  { value: '20s', label: '20代' },
  { value: '30s', label: '30代' },
  { value: '40s', label: '40代' },
  { value: '50s', label: '50代' },
  { value: '60s', label: '60代以上' },
  { value: 'mixed', label: '幅広い年代' },
];

const GENDER_RATIO_OPTIONS = [
  { value: '', label: '未設定' },
  { value: '男性多め', label: '男性多め' },
  { value: '女性多め', label: '女性多め' },
  { value: '半々くらい', label: '半々くらい' },
  { value: '男性のみ', label: '男性のみ' },
  { value: '女性のみ', label: '女性のみ' },
  { value: 'その他', label: 'その他' },
];

const ACTIVITY_STYLE_OPTIONS = [
  { value: 'beginner-friendly', label: '初心者歓迎' },
  { value: 'fun-oriented', label: '楽しさ重視' },
  { value: 'satoyama-focused', label: '里山中心' },
  { value: 'hyakumeizan-focused', label: '百名山中心' },
  { value: 'lightweight-oriented', label: '軽量志向' },
  { value: 'senior-friendly', label: 'シニア向け' },
  { value: 'mixed', label: 'いろいろな山を楽しむ' },
];

const CULTURE_OPTIONS = [
  { value: 'beginner_welcome', label: '初参加歓迎' },
  { value: 'no_pressure', label: '無理をしない' },
  { value: 'slow_pace', label: 'ゆっくり歩く' },
  { value: 'photo_friendly', label: '写真も楽しむ' },
  { value: 'food_after_hike', label: '下山後ごはん' },
  { value: 'retreat_positive', label: '撤退判断を大切にする' },
  { value: 'family_friendly', label: '家族参加歓迎' },
  { value: 'training', label: '体力づくり' },
];

const SAFETY_POLICY_OPTIONS = [
  { value: 'submit_plan', label: '登山計画書の提出を推奨する' },
  { value: 'gear_check', label: '装備チェックを事前に行う' },
  { value: 'weather_cancel', label: '悪天候時は中止・延期する' },
  { value: 'pace_control', label: '参加者のペースを優先する' },
  { value: 'emergency_contact', label: '緊急連絡先を確認する' },
  { value: 'insurance', label: '山岳保険加入を推奨する' },
];

const EMPTY_FORM = {
  groupName: '',
  tagline: '',
  prefecture: '',
  activityArea: '',
  activityFrequency: '',
  activityFrequencyOther: '',
  activityDays: '',
  currentMembers: 1,
  genderRatio: '',
  ageGroup: '',
  activityStyle: '',
  meetingPlace: '',
  memberRequirements: '',
  participationFlow: '',
  feeInfo: '',
  homepage: '',
  description: '',
  cultures: ['beginner_welcome', 'no_pressure', 'retreat_positive'],
  safetyPolicies: ['submit_plan', 'gear_check', 'weather_cancel'],
};

function CreateCircle() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const errorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
      ...(name === 'activityFrequency' && value !== 'other' ? { activityFrequencyOther: '' } : {}),
    }));
  };

  const toggleListValue = (name, value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      return {
        ...prev,
        [name]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setErrorAndScroll('画像は10MB以下にしてください。');
      return;
    }

    try {
      setFieldError('');
      const compressed = await compressImageFile(file, {
        maxWidth: 1280,
        maxHeight: 1280,
        quality: 0.85,
        outputType: 'image/jpeg',
      });
      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    } catch (error) {
      console.error('Circle image compression failed:', error);
      setErrorAndScroll('画像の圧縮に失敗しました。別の画像でお試しください。');
    }
  };

  const scrollToError = () => {
    window.setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const setErrorAndScroll = (message) => {
    setFieldError(message);
    scrollToError();
  };

  const validate = () => {
    if (!formData.groupName.trim()) return 'サークル名を入力してください。';
    if (!formData.prefecture) return '主な活動都道府県を選択してください。';
    if (!formData.activityFrequency) return '活動頻度を選択してください。';
    if (formData.activityFrequency === 'other' && !formData.activityFrequencyOther.trim()) {
      return '活動頻度の補足を入力してください。';
    }
    if (!formData.activityStyle) return '活動スタイルを選択してください。';
    if (!formData.description.trim()) return 'サークル紹介文を入力してください。';
    if (!formData.currentMembers || Number(formData.currentMembers) < 1) {
      return '現在のメンバー数は1人以上で入力してください。';
    }
    if (formData.groupName.length > 60) return 'サークル名は60文字以内で入力してください。';
    if (formData.tagline.length > 80) return '一言紹介は80文字以内で入力してください。';
    if (formData.activityArea.length > 80) return '主な活動エリアは80文字以内で入力してください。';
    if (formData.activityDays.length > 80) return '活動曜日・時間帯は80文字以内で入力してください。';
    if (formData.meetingPlace.length > 120) return '主な集合場所は120文字以内で入力してください。';
    if (formData.memberRequirements.length > 400) return '募集対象・参加条件は400文字以内で入力してください。';
    if (formData.participationFlow.length > 400) return '参加までの流れは400文字以内で入力してください。';
    if (formData.feeInfo.length > 300) return '費用・会費は300文字以内で入力してください。';
    if (formData.description.length > 1200) return 'サークル紹介文は1200文字以内で入力してください。';
    const homepageValidation = validateHttpsUrl(formData.homepage, 'homepage');
    if (!homepageValidation.isValid) return homepageValidation.message;
    return '';
  };

  const buildDescription = () => {
    const tagline = formData.tagline.trim();
    const activityArea = formData.activityArea.trim();
    const activityDays = formData.activityDays.trim();
    const meetingPlace = formData.meetingPlace.trim();
    const memberRequirements = formData.memberRequirements.trim();
    const participationFlow = formData.participationFlow.trim();
    const feeInfo = formData.feeInfo.trim();
    const cultureLabels = CULTURE_OPTIONS
      .filter((option) => formData.cultures.includes(option.value))
      .map((option) => option.label);
    const safetyLabels = SAFETY_POLICY_OPTIONS
      .filter((option) => formData.safetyPolicies.includes(option.value))
      .map((option) => option.label);

    return [
      tagline ? `【サークルの雰囲気】\n${tagline}` : '',
      activityArea ? `【主な活動エリア】\n${activityArea}` : '',
      activityDays ? `【活動曜日・時間帯】\n${activityDays}` : '',
      meetingPlace ? `【主な集合場所】\n${meetingPlace}` : '',
      memberRequirements ? `【募集対象・参加条件】\n${memberRequirements}` : '',
      participationFlow ? `【参加までの流れ】\n${participationFlow}` : '',
      feeInfo ? `【費用・会費】\n${feeInfo}` : '',
      `【紹介文】\n${formData.description.trim()}`,
      cultureLabels.length ? `【大切にしている文化】\n${cultureLabels.join(' / ')}` : '',
      safetyLabels.length ? `【安全方針】\n${safetyLabels.join(' / ')}` : '',
    ].filter(Boolean).join('\n\n');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentUser) {
      navigate('/signup?next=/create-circle');
      return;
    }

    const validationError = validate();
    if (validationError) {
      setErrorAndScroll(validationError);
      return;
    }

    setSubmitting(true);
    setFieldError('');

    try {
      const description = buildDescription();
      const moderation = await guardContentSubmission({
        action: 'recruitment_create',
        contentType: 'group_recruitment',
        texts: [
          formData.groupName,
          formData.tagline,
          formData.activityArea,
          formData.activityDays,
          formData.description,
          formData.activityFrequencyOther,
          formData.meetingPlace,
          formData.memberRequirements,
          formData.participationFlow,
          formData.feeInfo,
        ],
        metadata: { recruitmentType: 'group', source: 'create-circle' },
      });
      showModerationWarning(moderation);

      const existingCircleSnap = await getDocs(query(
        collection(db, 'recruitments'),
        where('userId', '==', currentUser.uid),
        where('recruitmentType', '==', 'group'),
        where('status', '==', 'active'),
        limit(1)
      ));

      if (!existingCircleSnap.empty) {
        const existingCircleId = existingCircleSnap.docs[0].id;
        showToast({
          message: 'すでに団体募集を作成済みです。管理画面を開きます。',
          type: 'info',
        });
        navigate(`/circles/${existingCircleId}/manage`);
        return;
      }

      const userProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userProfile = userProfileDoc.exists() ? userProfileDoc.data() : {};
      const organizer = {
        nickname: userProfile.nickname || currentUser.displayName || '登山ユーザー',
        photoURL: userProfile.photoURL || currentUser.photoURL || '',
        email: currentUser.email || '',
        uid: currentUser.uid,
      };

      let imageUrl = '';
      if (imageFile) {
        const storageRef = ref(storage, `recruitment_images/${currentUser.uid}_circle_${Date.now()}`);
        await uploadBytes(storageRef, imageFile, {
          contentType: imageFile.type || 'image/jpeg',
        });
        imageUrl = await getDownloadURL(storageRef);
      }

      const recruitmentData = {
        recruitmentType: 'group',
        source: 'circle_create',
        userId: currentUser.uid,
        organizer,
        status: 'active',
        applicationCount: 0,
        approvedCount: 0,
        imageUrl,
        groupName: formData.groupName.trim(),
        circleName: formData.groupName.trim(),
        circleTagline: formData.tagline.trim(),
        circleCultures: formData.cultures,
        circleSafetyPolicies: formData.safetyPolicies,
        activityArea: formData.activityArea.trim(),
        activityDays: formData.activityDays.trim(),
        meetingPlace: formData.meetingPlace.trim(),
        memberRequirements: formData.memberRequirements.trim(),
        participationFlow: formData.participationFlow.trim(),
        feeInfo: formData.feeInfo.trim(),
        maxParticipants: 0,
        description,
        prefecture: formData.prefecture,
        activityFrequency: formData.activityFrequency,
        activityFrequencyOther: formData.activityFrequencyOther.trim(),
        currentMembers: Number(formData.currentMembers),
        genderRatio: formData.genderRatio,
        ageGroup: formData.ageGroup,
        activityStyle: formData.activityStyle,
        homepage: formData.homepage.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = doc(db, 'recruitments', `circle_${currentUser.uid}`);
      const directExistingCircle = await getDoc(docRef);
      if (directExistingCircle.exists()) {
        showToast({
          message: 'すでに団体募集を作成済みです。管理画面を開きます。',
          type: 'info',
        });
        navigate(`/circles/${docRef.id}/manage`);
        return;
      }

      await setDoc(docRef, recruitmentData);

      await setDoc(
        doc(db, 'userStats', currentUser.uid),
        { recruitedCount: increment(1) },
        { merge: true }
      );

      // オーナーをメンバーに追加
      await addCircleMember(docRef.id, currentUser.uid, { nickname: organizer.nickname, photoURL: organizer.photoURL });

      showToast({ message: 'サークルを作成し、メンバー募集を公開しました。', type: 'success' });
      navigate(`/circles/${docRef.id}/manage`);
    } catch (error) {
      console.error('Circle creation failed:', error);
      setErrorAndScroll('サークルの作成に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-circle-page">
      <section className="create-circle-hero">
        <div className="create-circle-hero__copy">
          <p className="create-circle-hero__kicker">Yamatch Circle</p>
          <h1>登山サークルを作成する</h1>
          <p>
            サークルの雰囲気、安全方針、活動エリアを登録すると、
            ヤマッチの既存メンバー募集として公開されます。
          </p>
          <div className="create-circle-hero__actions">
            <a href="#circle-form" className="create-circle-btn create-circle-btn--primary">
              作成フォームへ
            </a>
            <Link to="/recruitments?type=group" className="create-circle-btn create-circle-btn--ghost">
              募集中のサークルを見る
            </Link>
          </div>
        </div>
        <div className="create-circle-hero__panel" aria-label="公開される情報の概要">
          <div>
            <span>公開先</span>
            <strong>登山サークル・メンバー募集一覧</strong>
          </div>
          <div>
            <span>応募管理</span>
            <strong>既存の応募・承認・チャット機能を利用</strong>
          </div>
          <div>
            <span>作成できる人</span>
            <strong>ヤマッチに登録したユーザー</strong>
          </div>
        </div>
      </section>

      {!currentUser && (
        <div className="create-circle-login-callout">
          <div>
            <strong>作成にはログインが必要です</strong>
            <p>フォーム内容は確認できます。公開するには無料登録またはログインしてください。</p>
          </div>
          <div className="create-circle-login-callout__actions">
            <Link to="/signup?next=/create-circle" className="create-circle-btn create-circle-btn--primary">
              無料登録
            </Link>
            <Link to="/login?next=/create-circle" className="create-circle-btn create-circle-btn--ghost">
              ログイン
            </Link>
          </div>
        </div>
      )}

      <form id="circle-form" className="create-circle-form" onSubmit={handleSubmit} noValidate>
        {fieldError && (
          <div
            ref={errorRef}
            className="create-circle-error"
            tabIndex="-1"
            role="alert"
            aria-live="assertive"
          >
            {fieldError}
          </div>
        )}

        <section className="create-circle-section">
          <div className="create-circle-section__head">
            <span>01</span>
            <h2>基本情報</h2>
          </div>
          <div className="create-circle-grid">
            <label className="create-circle-field">
              <span>サークル名 *</span>
              <input
                name="groupName"
                value={formData.groupName}
                onChange={handleChange}
                maxLength={60}
                placeholder="例: ゆるハイク東京"
                required
              />
            </label>
            <label className="create-circle-field">
              <span>一言紹介</span>
              <input
                name="tagline"
                value={formData.tagline}
                onChange={handleChange}
                maxLength={80}
                placeholder="例: 無理せず、楽しく。低山中心のサークルです。"
              />
            </label>
            <label className="create-circle-field">
              <span>主な活動都道府県 *</span>
              <select name="prefecture" value={formData.prefecture} onChange={handleChange} required>
                <option value="">選択してください</option>
                {PREFECTURES.map((prefecture) => (
                  <option key={prefecture} value={prefecture}>{prefecture}</option>
                ))}
              </select>
            </label>
            <label className="create-circle-field">
              <span>主な活動エリア</span>
              <input
                name="activityArea"
                value={formData.activityArea}
                onChange={handleChange}
                placeholder="例: 高尾・奥多摩・丹沢"
              />
            </label>
            <label className="create-circle-field">
              <span>主な集合場所</span>
              <input
                name="meetingPlace"
                value={formData.meetingPlace}
                onChange={handleChange}
                maxLength={120}
                placeholder="例: 新宿駅、現地登山口、都内主要駅"
              />
            </label>
          </div>
        </section>

        <section className="create-circle-section">
          <div className="create-circle-section__head">
            <span>02</span>
            <h2>募集条件</h2>
          </div>
          <div className="create-circle-grid">
            <label className="create-circle-field">
              <span>活動頻度 *</span>
              <select name="activityFrequency" value={formData.activityFrequency} onChange={handleChange} required>
                <option value="">選択してください</option>
                {ACTIVITY_FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="create-circle-field">
              <span>活動曜日・時間帯</span>
              <input
                name="activityDays"
                value={formData.activityDays}
                onChange={handleChange}
                maxLength={80}
                placeholder="例: 土日祝中心 / 平日夜に計画相談"
              />
            </label>
            <label className="create-circle-field">
              <span>活動頻度の補足</span>
              <input
                name="activityFrequencyOther"
                value={formData.activityFrequencyOther}
                onChange={handleChange}
                placeholder="例: 月1から2回、土日中心"
                disabled={formData.activityFrequency !== 'other'}
              />
            </label>
            <label className="create-circle-field">
              <span>現在のメンバー数 *</span>
              <input
                type="number"
                name="currentMembers"
                value={formData.currentMembers}
                onChange={handleChange}
                min="1"
                required
              />
            </label>
            <label className="create-circle-field">
              <span>年齢層</span>
              <select name="ageGroup" value={formData.ageGroup} onChange={handleChange}>
                <option value="">未設定</option>
                {AGE_GROUP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="create-circle-field">
              <span>男女比</span>
              <select
                name="genderRatio"
                value={formData.genderRatio}
                onChange={handleChange}
              >
                {GENDER_RATIO_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="create-circle-field">
              <span>活動スタイル *</span>
              <select name="activityStyle" value={formData.activityStyle} onChange={handleChange} required>
                <option value="">選択してください</option>
                {ACTIVITY_STYLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <section className="create-circle-section">
          <div className="create-circle-section__head">
            <span>03</span>
            <h2>参加条件</h2>
          </div>
          <div className="create-circle-grid">
            <label className="create-circle-field create-circle-field--full">
              <span>募集対象・参加条件</span>
              <textarea
                name="memberRequirements"
                value={formData.memberRequirements}
                onChange={handleChange}
                rows={5}
                maxLength={400}
                placeholder="例: 初心者歓迎。登山靴・レインウェアなど基本装備を用意できる方。未成年の方は保護者同伴でお願いします。"
              />
            </label>
            <label className="create-circle-field create-circle-field--full">
              <span>参加までの流れ</span>
              <textarea
                name="participationFlow"
                value={formData.participationFlow}
                onChange={handleChange}
                rows={5}
                maxLength={400}
                placeholder="例: ヤマッチから応募 → 簡単な自己紹介 → 初回は日帰り低山に体験参加 → 継続参加を相談"
              />
            </label>
            <label className="create-circle-field create-circle-field--full">
              <span>費用・会費</span>
              <textarea
                name="feeInfo"
                value={formData.feeInfo}
                onChange={handleChange}
                rows={4}
                maxLength={300}
                placeholder="例: 会費なし。交通費・食費・保険料は各自負担。イベントにより実費を事前案内します。"
              />
            </label>
          </div>
        </section>

        <section className="create-circle-section">
          <div className="create-circle-section__head">
            <span>04</span>
            <h2>文化と安全方針</h2>
          </div>
          <div className="create-circle-pick-block">
            <h3>このサークルが大切にすること</h3>
            <div className="create-circle-chips">
              {CULTURE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={formData.cultures.includes(option.value) ? 'is-selected' : ''}
                  onClick={() => toggleListValue('cultures', option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="create-circle-pick-block">
            <h3>安全方針</h3>
            <div className="create-circle-chips create-circle-chips--safety">
              {SAFETY_POLICY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={formData.safetyPolicies.includes(option.value) ? 'is-selected' : ''}
                  onClick={() => toggleListValue('safetyPolicies', option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="create-circle-section">
          <div className="create-circle-section__head">
            <span>05</span>
            <h2>紹介文と画像</h2>
          </div>
          <label className="create-circle-field create-circle-field--full">
            <span>サークル紹介文 *</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={8}
              maxLength={1200}
              placeholder="活動内容、参加しやすい人、初参加の流れ、注意事項などを書いてください。"
              required
            />
          </label>
          <div className="create-circle-grid">
            <label className="create-circle-field">
              <span>ホームページ・SNS</span>
              <input
                type="url"
                name="homepage"
                value={formData.homepage}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </label>
            <div className="create-circle-image-field">
              <span>カバー画像</span>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview ? (
                <div className="create-circle-image-preview">
                  <img src={imagePreview} alt="サークル画像プレビュー" />
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview('');
                    }}
                  >
                    画像を削除
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => imageInputRef.current?.click()}>
                  画像を選択
                </button>
              )}
            </div>
          </div>
        </section>

        <div className="create-circle-submit">
          <p>公開後は「募集履歴」から編集できます。応募者対応は既存の募集詳細画面で行います。</p>
          <button type="submit" className="create-circle-btn create-circle-btn--primary" disabled={submitting}>
            {submitting ? '公開中...' : 'サークルを作成して募集を公開'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateCircle;
